import { ThemeToggle, useTheme } from "@/components/theme-provider";
import { cn } from "@/lib/utils";
import { Moon, PanelLeftClose, PanelLeftOpen, Sun } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";

/** Collapsed/expanded state for the desktop sidebar, persisted so the choice survives reloads
 *  (mirrors how the theme is stored). Defaults to expanded. Shared by every app shell. */
export function useSidebarCollapsed(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem("ow-sidebar-collapsed") === "1";
  });
  useEffect(() => {
    if (typeof localStorage !== "undefined") localStorage.setItem("ow-sidebar-collapsed", collapsed ? "1" : "0");
  }, [collapsed]);
  const toggle = useCallback(() => setCollapsed((c) => !c), []);
  return [collapsed, toggle];
}

/** The class for one sidebar nav row — idle / hover / active, all built on foreground overlays so the
 *  three states read the same in light and dark. The link element itself is supplied by the caller
 *  (the client uses RoleLink, admin uses Link), so this stays free of routing types: spread the class
 *  on the link and drop `NavLinkInner` inside it. */
export function navLinkClass(active: boolean, collapsed: boolean): string {
  return cn(
    "group flex items-center rounded-md py-2 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
    collapsed ? "justify-center px-0" : "gap-3 px-2",
    active
      ? "bg-foreground/[0.10] font-medium text-foreground"
      : "text-foreground/65 hover:bg-foreground/[0.05] hover:text-foreground",
  );
}

/** The inside of a nav row: icon + label + an optional live count badge. Collapsed drops the label
 *  (moved to a native tooltip by the caller) and the count for an icon-only rail. */
export function NavLinkInner({
  icon: Icon,
  label,
  collapsed,
  count,
  active,
}: { icon: LucideIcon; label: string; collapsed: boolean; count?: number; active?: boolean }) {
  return (
    <>
      <Icon className="size-4 shrink-0" />
      {collapsed ? null : <span className="flex-1 truncate">{label}</span>}
      {!collapsed && count != null && count > 0 ? (
        <span
          className={cn(
            "shrink-0 rounded-full px-1.5 py-0.5 text-[11px] leading-none tabular-nums transition-colors",
            active
              ? "bg-foreground/15 text-foreground"
              : "bg-foreground/[0.06] text-foreground/60 group-hover:bg-foreground/15 group-hover:text-foreground",
          )}
        >
          {count}
        </span>
      ) : null}
    </>
  );
}

/** Appearance as a single quiet menu row — icon, label, current value. Shared by the client and admin
 *  footers so the theme control reads identically in both. */
export function ThemeMenuRow() {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
      className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm text-foreground/65 outline-none transition-colors hover:bg-foreground/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      {isDark ? <Moon className="size-4 shrink-0" /> : <Sun className="size-4 shrink-0" />}
      <span className="flex-1 text-left">Appearance</span>
      <span className="font-mono text-[11px] uppercase leading-none tracking-wide text-muted-foreground">
        {isDark ? "Dark" : "Light"}
      </span>
    </button>
  );
}

/** The shared application frame: a persistent, collapsible left sidebar (brand + nav + footer) with a
 *  slim mobile top bar below `md`, and the scrolling content column. Both the client portal and the
 *  admin back-office render through this one shell so the two read as a single product (audit P8);
 *  each supplies its own brand lockup, nav, footer, mobile bar, and content width. */
export function AppShell({
  brand,
  nav,
  footer,
  collapsedFooter,
  mobile,
  mainClassName,
  children,
}: {
  brand: ReactNode;
  nav: (collapsed: boolean) => ReactNode;
  footer?: (collapsed: boolean) => ReactNode;
  collapsedFooter?: ReactNode;
  mobile?: ReactNode;
  mainClassName?: string;
  children: ReactNode;
}) {
  const [collapsed, toggle] = useSidebarCollapsed();
  return (
    <div className="min-h-screen md:flex">
      <aside
        className={cn(
          "sticky top-0 z-30 hidden h-screen shrink-0 flex-col border-r bg-sidebar transition-[width] duration-200 md:flex",
          collapsed ? "w-16" : "w-60",
        )}
      >
        <div
          className={cn("flex h-16 items-center border-b", collapsed ? "justify-center px-2" : "justify-between px-4")}
        >
          {collapsed ? null : brand}
          {/* Standalone glyph — no button box or padding, so it sits as a bare icon at the sidebar gutter. */}
          <button
            type="button"
            onClick={toggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            {collapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
          </button>
        </div>
        <nav className="flex-1 px-2 py-3">{nav(collapsed)}</nav>
        {collapsed ? (
          <div className="flex flex-col items-center gap-2 border-t p-2">{collapsedFooter ?? <ThemeToggle />}</div>
        ) : footer ? (
          <div className="mt-auto border-t px-2 py-3">{footer(collapsed)}</div>
        ) : null}
      </aside>
      <div className="min-w-0 flex-1">
        {mobile}
        <main className={mainClassName ?? "mx-auto max-w-[1080px] px-5 py-8 md:px-10 md:py-10"}>{children}</main>
      </div>
    </div>
  );
}
